import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface ListingsHealthRow {
  coverage_min_date: string | null
  coverage_max_date: string | null
  forward_coverage_days: number
  last_success_at: string | null
  last_attempt_at: string | null
  last_status: string | null
  last_error: string | null
  last_run_trigger_type: string | null
  last_run_started_at: string | null
  last_run_finished_at: string | null
  last_run_error: string | null
  success_count_last_10: number
  total_runs_last_10: number
  next_run_at: string | null
}

export interface ListingsHealthChannelRow {
  channel_id: string
  channel_name: string
  is_displayed: boolean
  gap_status: string
}

export interface AdminListingsHealthResult {
  health: ListingsHealthRow | null
  channels: ListingsHealthChannelRow[]
}

export function useAdminListingsHealth() {
  return useQuery({
    queryKey: ['admin-listings-health'],
    queryFn: async (): Promise<AdminListingsHealthResult> => {
      const [healthRes, channelsRes] = await Promise.all([
        supabase.rpc('get_admin_listings_health'),
        supabase.rpc('get_admin_listings_health_channels'),
      ])
      if (healthRes.error) throw healthRes.error
      if (channelsRes.error) throw channelsRes.error

      const healthData = Array.isArray(healthRes.data) ? healthRes.data[0] : healthRes.data
      const health: ListingsHealthRow | null = healthData
        ? {
            coverage_min_date: healthData.coverage_min_date ?? null,
            coverage_max_date: healthData.coverage_max_date ?? null,
            forward_coverage_days: Number(healthData.forward_coverage_days ?? 7),
            last_success_at: healthData.last_success_at ?? null,
            last_attempt_at: healthData.last_attempt_at ?? null,
            last_status: healthData.last_status ?? null,
            last_error: healthData.last_error ?? null,
            last_run_trigger_type: healthData.last_run_trigger_type ?? null,
            last_run_started_at: healthData.last_run_started_at ?? null,
            last_run_finished_at: healthData.last_run_finished_at ?? null,
            last_run_error: healthData.last_run_error ?? null,
            success_count_last_10: Number(healthData.success_count_last_10 ?? 0),
            total_runs_last_10: Number(healthData.total_runs_last_10 ?? 0),
            next_run_at: healthData.next_run_at ?? null,
          }
        : null

      const channelRows = (Array.isArray(channelsRes.data) ? channelsRes.data : []) as Array<{
        channel_id: string
        channel_name: string
        is_displayed: boolean
        gap_status: string
      }>
      const channels: ListingsHealthChannelRow[] = channelRows.map((r) => ({
        channel_id: r.channel_id,
        channel_name: r.channel_name ?? r.channel_id,
        is_displayed: Boolean(r.is_displayed),
        gap_status: r.gap_status ?? 'OK',
      }))

      return { health, channels }
    },
    staleTime: 60_000,
  })
}
